/**
 * PropertyDetailsModal
 * ─────────────────────────────────────────────────────────────
 * iter-alignment · Property Quick-View Redesign (2026-02-19)
 *
 * Live operational transaction snapshot, sourced exclusively
 * from the canonical Property / Deal / Participant / NBA
 * SSOTs in chainStore + the `/api/deals` envelope.  No new
 * endpoints; no duplicate recommendation logic; the modal is
 * presentational — every field falls back to a calm em-dash
 * when the SSOT is silent.
 *
 * Anatomy (per the approved reference):
 *
 *   1. Header — 128px image · UNDER OFFER pill · full
 *      canonical address · postcode subline · price + tenure
 *      · risk badge · Last chased · View activity link.
 *   2. Compact parties row — Buyer · Seller · Buyer Solicitor
 *      · Seller Solicitor · Broker (conditional).
 *   3. 3-column summary — Transaction Progress (% + N/M key
 *      journey stages) · Predicted Exchange (date + slim
 *      confidence bar) · Target Dates (Exchange + Completion).
 *   4. Prominent violet Next Best Action row — canonical NBA,
 *      responsible party, waiting/last-chased, Chase now CTA.
 *   5. Shallow single-row Recent Activity (capped 4).
 *   6. Footer — Open Deal Room · Documents · Risk register.
 *
 * Motion: restrained fade-up shell + backdrop fade, both gated
 * by `prefers-reduced-motion`.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { saveDealReturnState } from "../lib/dealReturnState";
import {
  X, ArrowRight, MapPin, Calendar, Users, ShieldCheck, Sparkles,
  FileText, MessageSquare, Activity, Check, Clock, User, Plus,
  Home, Briefcase, Scale, Landmark, Tag,
} from "lucide-react";
import { useChainStore, openCreateDeal } from "../data/chainStore";
import { STAGE_PROGRESS_NOTE } from "./StageProgressHint";
import {
  journeyFor,
  optsForDeal,
  closeCompletedSet,
  activeIndexFor,
  PRE_JOURNEY,
} from "../data/transactionJourney";
import { classifyDeal } from "../data/dealHealth";

/* ── Tone helpers ─────────────────────────────────────────────── */
const RISK_TONE = {
  low:  { dot: "#10B981", text: "text-emerald-700", bg: "bg-emerald-50", ring: "ring-emerald-200/70", label: "LOW RISK",  ringColor: "rgba(16,185,129,0.30)" },
  med:  { dot: "#F59E0B", text: "text-amber-700",   bg: "bg-amber-50",   ring: "ring-amber-200/70",   label: "WATCH",      ringColor: "rgba(245,158,11,0.30)" },
  high: { dot: "#F43F5E", text: "text-rose-700",    bg: "bg-rose-50",    ring: "ring-rose-200/70",    label: "AT RISK",    ringColor: "rgba(244,63,94,0.30)" },
};

const PARTY_DOT = {
  buyer:    "#8B5CF6", // violet
  seller:   "#EC4899", // pink
  solicitor:"#F59E0B", // amber
  broker:   "#10B981", // emerald
};

/* iter-alignment · Property Quick-View Redesign (2026-02-19)
 * — MEMORY_TONE retired.  The redesigned modal reads activity
 * tones from `_activityToneClasses` at the bottom of this file. */

/* ── Calm soft progress ring used in the Momentum card ──────────── */
const SoftRing = ({ value = 60, size = 96, stroke = 5, color = "#8B5CF6" }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, value)) / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="property-modal-progress-ring">
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(226,232,240,0.7)" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="font-bold tabular-nums text-slate-900" style={{ fontSize: size * 0.24 }} data-testid="property-modal-progress-pct">{value}%</span>
        <span className="mt-0.5 font-bold uppercase text-slate-400" style={{ fontSize: size * 0.10, letterSpacing: "0.1em" }}>Progress</span>
      </div>
    </div>
  );
};

/* iter-alignment · Property Quick-View Redesign (2026-02-19) —
 * MemoryItem + MEMORY_TONE retired.  The redesigned modal renders
 * a shallower Recent Activity row via the inline helpers below
 * (`_activityToneClasses` / `_activityIcon`). */

export const PropertyDetailsModal = () => {
  const [deal, setDeal] = useState(null);
  const nav = useNavigate();
  const { deals, findContact, findProperty } = useChainStore();

  useEffect(() => {
    const onOpen = (e) => setDeal(e.detail);
    const onKey = (e) => { if (e.key === "Escape") setDeal(null); };
    window.addEventListener("cf:property:open", onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("cf:property:open", onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  /* iter-241 — Hardened resolver for the canonical Deal Room ID.
   * Extends iter-221C with explicit rejection of placeholder /
   * draft / sample / listing IDs, plus a guarantee that the
   * resolved ID actually exists in the live deals index so the
   * navigation can NEVER hit the "Deal Room not found" page.
   *
   * Resolution order (first valid wins):
   *   1. Explicit property-side links — property.deal_id /
   *      property.transaction_id (the canonical FK shape).
   *   2. Explicit event-payload override — deal.dealId / deal_id /
   *      canonical_deal_room_id / transaction_id (legacy callers).
   *   3. propertyId/property_id lookup against chainStore.deals.
   *   4. Direct match against chainStore.deals.id (caller already
   *      passed a deal id, e.g. from DealRow / PriorityDeals).
   *
   * Every candidate is gated through `isValidCanonicalDealId()`
   * — rejects null/undefined, sample/draft/listing prefixes, and
   * any ID that doesn't actually exist in chainStore.deals. */
  const resolvedDealId = useMemo(() => {
    if (!deal) return null;
    const list = Array.isArray(deals) ? deals : [];

    // Reject obvious placeholder shapes BEFORE we ever route on them.
    const PLACEHOLDER_PREFIXES = ["deal-", "p-", "draft-", "listing-", "sample-", "demo-"];
    const isValidCanonicalDealId = (candidate) => {
      if (!candidate || typeof candidate !== "string") return false;
      const trimmed = candidate.trim();
      if (!trimmed) return false;
      if (PLACEHOLDER_PREFIXES.some((p) => trimmed.toLowerCase().startsWith(p))) return false;
      // Must correspond to a real deal in the live workspace index.
      return list.some((d) => d && d.id === trimmed);
    };

    // 1 + 2 · Explicit overrides from either side of the payload.
    const explicitCandidates = [
      deal.deal_id,
      deal.transaction_id,
      deal.dealId,
      deal.canonical_deal_room_id,
    ];
    for (const c of explicitCandidates) {
      if (isValidCanonicalDealId(c)) return c;
    }

    if (!deal.id) return null;

    // 3 · propertyId / property_id lookup — caller passed a property.
    const byProperty = list.find(
      (d) => d && (d.propertyId === deal.id || d.property_id === deal.id)
    );
    if (byProperty?.id && isValidCanonicalDealId(byProperty.id)) return byProperty.id;

    // 4 · Direct ID match — caller already passed a deal id.
    const byDirect = list.find((d) => d && d.id === deal.id);
    if (byDirect?.id && isValidCanonicalDealId(byDirect.id)) return byDirect.id;

    return null;
  }, [deal, deals]);

  /* iter-alignment · Property Quick-View Redesign (2026-02-19)
   * ────────────────────────────────────────────────────────
   * The modal reads exclusively from the canonical SSOTs:
   *   • `deals` / `findProperty` / `findContact` in chainStore
   *   • `deal.waiting_on` / `deal.progress` / `deal.target_*`
   *     shipped by `/api/deals` (see routes_deals.py)
   *   • the payload passed via `cf:property:open` for legacy
   *     callers.  We NEVER fabricate participants, target dates
   *     or NBA copy — we render whatever the SSOT surfaces and
   *     fall back to a calm em-dash if it's absent.
   *
   * Both hooks sit ABOVE the early return so they honour the
   * Rules of Hooks (order preserved across renders). */
  const canonicalDeal = useMemo(() => {
    if (!resolvedDealId) return null;
    return (Array.isArray(deals) ? deals : []).find((d) => d && d.id === resolvedDealId) || null;
  }, [resolvedDealId, deals]);
  const canonicalProperty = useMemo(() => {
    if (!canonicalDeal || !findProperty) return null;
    return findProperty(canonicalDeal.propertyId) || null;
  }, [canonicalDeal, findProperty]);

  // Same source as the Deal Room Participants card: the deal's own participant rows.
  const location = useLocation();
  const [roomParticipants, setRoomParticipants] = useState(null);
  useEffect(() => {
    setRoomParticipants(null);
    if (!resolvedDealId) return undefined;
    let alive = true;
    api.get(`/api/v2/deals/${resolvedDealId}/participants`)
      .then(({ data }) => { if (alive) setRoomParticipants(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setRoomParticipants(null); });
    return () => { alive = false; };
  }, [resolvedDealId, deal]);

  if (!deal) return null;

  // Canonical risk rule (same classifyDeal the Deal Rooms list uses).
  const _cls = classifyDeal(canonicalDeal || deal);
  const risk = _cls.atRisk ? RISK_TONE.high : _cls.watch ? RISK_TONE.med : (RISK_TONE[deal.risk] || RISK_TONE.low);
  const close = () => setDeal(null);
  const hasRoom = Boolean(resolvedDealId);
  const openRoom = () => {
    if (!resolvedDealId) return;
    close();
    nav(`/deal-rooms/${resolvedDealId}`);
  };
  const startCreateDeal = () => {
    close();
    // Pre-fill the create-deal flow with the property we're on so
    // the operator doesn't re-key the address.
    openCreateDeal({ propertyId: deal.id, address: deal.address });
  };
  const openDocs = () => { close(); nav("/documents"); };
  const openRisk = () => {
    if (!resolvedDealId) { close(); return; }
    close();
    nav(`/deal-rooms/${resolvedDealId}?tab=risk`);
  };
  const openActivity = () => {
    if (!resolvedDealId) { close(); return; }
    close();
    nav(`/deal-rooms/${resolvedDealId}#activity`);
  };

  // Party name resolution — canonical Contact FKs first, then
  // fall back to the payload's ready-made strings for legacy
  // callers (PriorityDeals / DealRow) that don't pass a full
  // deal-id.  Accept both camelCase (chainStore-hydrated) and
  // snake_case (raw `/api/deals` envelope) so the modal keeps
  // resolving whichever alias the caller supplies.
  //
  // iter-alignment · P0 · Participant SSOT Fanout (2026-02-28)
  // — Fix for "Buyer/Seller/Buyer Solicitor/Seller Solicitor
  // display '—' even when the participant exists elsewhere in
  // the transaction."  Root cause: the deal-shape's six FK
  // columns (`buyer_id`, `seller_id`, `buyer_solicitor_id`, …)
  // are populated ONLY by CreateDealModal.  Participants added
  // AFTER creation via the Roles tab write to Postgres
  // `deal_participants` (typed) and never back-fill the FK
  // columns.  Fix: read `canonicalDeal.participants` (typed
  // rows fanned out on every `/api/deals` response) and resolve
  // the role → contact directly, before falling back to the
  // string label.  No competing fetch, no duplicate state,
  // no hardcoded names.  Multi-party support: for roles that
  // may legitimately have multiple entries (Buyer / Seller for
  // joint transactions) we join the resolved names on " · ".
  const _nameFromContact = (cid) => {
    if (!cid || !findContact) return null;
    const c = findContact(cid);
    if (!c) return null;
    return c.name || c.fullName || [c.firstName, c.lastName].filter(Boolean).join(" ") || null;
  };
  const _participantRows = Array.isArray(roomParticipants)
    ? roomParticipants
    : (Array.isArray(canonicalDeal?.participants) ? canonicalDeal.participants : []);
  // Every person holding a role, by saved contact ID. Legacy FK / payload strings only when no participant rows exist.
  const _peopleForRole = (roleType, fkId, legacyName) => {
    const seen = new Set();
    const people = _participantRows
      .filter((p) => p && String(p.participant_type || "").toLowerCase() === roleType)
      .map((p) => ({
        key: p.id || p.contact_id || p.full_name,
        contactId: p.contact_id || null,
        name: _nameFromContact(p.contact_id) || p.full_name || null,
      }))
      .filter((x) => x.name && !(x.contactId && seen.has(x.contactId)) && (x.contactId ? seen.add(x.contactId) : true));
    if (people.length) return people;
    const fkName = _nameFromContact(fkId);
    if (fkName) return [{ key: fkId, contactId: fkId, name: fkName }];
    return legacyName ? [{ key: legacyName, contactId: null, name: legacyName }] : [];
  };
  const buyers           = _peopleForRole("buyer", canonicalDeal?.buyerId || canonicalDeal?.buyer_id, deal.buyer);
  const sellers          = _peopleForRole("seller", canonicalDeal?.sellerId || canonicalDeal?.seller_id, deal.seller);
  const buyerSolicitors  = _peopleForRole("buyer_solicitor", canonicalDeal?.buyerSolicitorId || canonicalDeal?.buyer_solicitor_id, deal.buyerSolicitor || deal.solicitor);
  const sellerSolicitors = _peopleForRole("seller_solicitor", canonicalDeal?.sellerSolicitorId || canonicalDeal?.seller_solicitor_id, deal.sellerSolicitor);
  const brokers          = _peopleForRole("mortgage_broker", canonicalDeal?.brokerId || canonicalDeal?.broker_id, deal.broker);
  const openContact = (contactId) => {
    if (!contactId) return;
    const fromPath = `${location.pathname}${location.search}${location.hash}`;
    if (resolvedDealId && location.pathname.startsWith(`/deal-rooms/${resolvedDealId}`)) {
      saveDealReturnState(resolvedDealId, { modal: deal });
    }
    close();
    nav(`/contacts/${contactId}`, { state: { fromDeal: { path: fromPath } } });
  };

  // iter-alignment · Deal Summary Modal · SSOT Convergence
  // (2026-02-19) — Transaction Progress converges onto the SAME
  // canonical Key Journey Stages calculation the Deal Room's
  // right-rail card uses.  Denominator = applicable key stages
  // for this deal (Mortgage / Contract / Mgmt Pack / BSA /
  // Enquiries / Exchange / Completion, filtered by
  // `optsForDeal` — cash-buyer skips Mortgage, freehold skips
  // Mgmt Pack + BSA, explicit `applicableBSA:false` suppresses
  // BSA).  Numerator = applicable stages present in the closed
  // `completedSet`, seeded from the deal's `completed_steps` /
  // `completedSteps` and closure-inferred by
  // `closeCompletedSet` — the exact same helper the Deal Room
  // consumes.  No parallel taxonomy; no fixture patching.
  const _dealForJourney = canonicalDeal || deal || {};
  const _tenureLc = String(
    canonicalProperty?.tenure
    || _dealForJourney?.tenure
    || _dealForJourney?.property?.tenure
    || _dealForJourney?.property_tenure
    || "freehold",
  ).toLowerCase();
  const _journeySteps = journeyFor(_tenureLc || "freehold", optsForDeal(_dealForJourney, canonicalProperty));
  const _completedSrc = new Set([
    ...(Array.isArray(_dealForJourney?.completedSteps) ? _dealForJourney.completedSteps : []),
    ...(Array.isArray(_dealForJourney?.completed_steps) ? _dealForJourney.completed_steps : []),
    ...(Array.isArray(_dealForJourney?.completedStepsApi) ? _dealForJourney.completedStepsApi : []),
  ]);
  const _stageLcForJourney = String(_dealForJourney?.stage || _dealForJourney?.status || "offer").toLowerCase();
  const _exchangedFlag =
    !!_dealForJourney?.exchanged_at || !!_dealForJourney?.exchangedAt
    || _stageLcForJourney === "exchanged" || _stageLcForJourney === "completed"
    || _completedSrc.has("exchange");
  const _completedFlag =
    !!_dealForJourney?.completed_at || !!_dealForJourney?.completedAt
    || _stageLcForJourney === "completed";
  const _baselineIdx = activeIndexFor(_tenureLc || "freehold", _stageLcForJourney, optsForDeal(_dealForJourney, canonicalProperty));
  const _closedSet = closeCompletedSet({
    nodes: _journeySteps,
    completedSet: _completedSrc,
    exchangedFlag: _exchangedFlag,
    completedFlag: _completedFlag,
    baselineIdx: _baselineIdx,
  });
  const requiredTotal = _journeySteps.length;
  const requiredDone = _journeySteps.filter((s) => s?.id && _closedSet.has(s.id)).length;
  const progressPct = requiredTotal > 0
    ? Math.round((requiredDone / requiredTotal) * 100)
    : 0;

  // Predicted exchange + confidence — canonical projection.  The
  // canonical `/api/deals` envelope wins over caller-passed
  // payload so the modal never renders a stale prediction.
  const predictedIso    = canonicalDeal?.predicted_exchange || canonicalDeal?.predictedExchange || deal.predictedExchange || null;
  const predictedLabel  = (predictedIso ? _fmtDate(predictedIso) : null) || deal.exchangeDate || deal.predicted || "TBC";
  const confidencePct   = Math.max(0, Math.min(100, Number(
    canonicalDeal?.forecast_confidence ?? canonicalDeal?.forecastConfidence ?? deal.confidencePct ?? 0
  )));
  const confidenceLabel = confidencePct >= 75 ? "Strong"
    : confidencePct >= 50 ? "Moderate"
    : confidencePct > 0 ? "Fragile"
    : (deal.confidenceLabel || "—");

  // Target dates — canonical `target_exchange` / `target_completion`
  // are the SSOT; caller payload is only used as a legacy fallback
  // when the canonical envelope is silent.
  const targetExchange   = _fmtDate(canonicalDeal?.targetExchange   || canonicalDeal?.target_exchange)   || deal.targetExchange   || null;
  const targetCompletion = _fmtDate(canonicalDeal?.targetCompletion || canonicalDeal?.target_completion) || deal.targetCompletion || null;

  // Next Best Action — iter-alignment · Deep NBA Payload
  // (2026-02-19).  Consumes the canonical `next_best_action`
  // envelope from `/api/deals` (computed by
  // `_derive_next_best_action` in the backend).  The
  // `waiting_on` shape is preserved separately so the
  // responsible party line still reads correctly for blocked
  // rows.  The canonical label ALWAYS beats the caller payload
  // — a cash-buyer deal must never render a stale "Chase
  // broker" label from a legacy fixture.
  const canonicalNba = canonicalDeal?.next_best_action || canonicalDeal?.nextBestAction || null;
  const waitingOn    = canonicalDeal?.waiting_on || canonicalDeal?.waitingOn || null;
  const waitingParty = canonicalNba?.owner_role
    ? String(canonicalNba.owner_role).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : waitingOn?.owner_role
      ? String(waitingOn.owner_role).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : null;
  const nbaHeadline  = canonicalNba?.label
    || (waitingOn?.milestone_label ? `Chase ${waitingOn.milestone_label.toLowerCase()}` : null)
    || deal.nba
    || "Progress next milestone";
  const nbaWaitingDays = Number(canonicalDeal?.waiting_days ?? canonicalDeal?.waitingDays ?? deal.waitingDays ?? 0);
  const stage = canonicalDeal?.stage || deal.stage || "Under Offer";
  // Same wording as the Deal Room journey's "Recorded stage".
  const _preStep = PRE_JOURNEY.find((p) => p.stages.includes(_stageLcForJourney));
  // Current step = first applicable step not yet closed (shows Management Pack / BSA like the Deal Room).
  const _activeIdx = _journeySteps.findIndex((s, i) => i >= Math.max(0, _baselineIdx) && !_closedSet.has(s.id));
  const stageLabel = _preStep?.longLabel
    || _journeySteps[_activeIdx >= 0 ? _activeIdx : _baselineIdx]?.label || _prettyStage(stage);
  const lastChasedRel = _relFromIso(canonicalDeal?.last_chased_at || canonicalDeal?.lastChasedAt) || deal.lastChasedRel || "—";

  // Price + tenure — canonical property fields with legacy shim.
  /* iter-alignment · P0 · Canonical Price For Property (2026-02-28)
   * ---------------------------------------------------------------
   * Prefer the deal-level agreed price whenever it's persisted —
   * once a deal has an accepted offer, that agreed price is the
   * operational number every consumer surface must display.  Only
   * fall through to the property's asking price (and the legacy
   * `deal.value` / `deal.price` shims) when no agreed number has
   * been persisted yet.  Property Asking Price is never mutated by
   * this read. */
  const priceLabel = _priceLabel(
    canonicalDeal?.agreedPrice
    ?? canonicalDeal?.agreed_price
    ?? canonicalProperty?.price
    ?? deal.value
    ?? deal.price
  );
  const tenureLabel = (canonicalProperty?.tenure || deal.tenure || "").toString();
  const tenurePretty = tenureLabel ? tenureLabel.charAt(0).toUpperCase() + tenureLabel.slice(1) : null;

  // Full canonical address — Property.address as primary, then
  // Property.postcode / office as the geo subline.
  const addressPrimary = canonicalProperty?.address || deal.address || "Property";
  const addressSubline = (() => {
    const parts = [];
    const office = canonicalProperty?.office || deal.office;
    if (office) parts.push(office.replace(/^London — /, "London,"));
    const postcode = canonicalProperty?.postcode || deal.postcode;
    if (postcode) parts.push(postcode);
    return parts.join(", ");
  })();

  // Recent activity — canonical `deal.recent_activity`, capped at 4.
  const activities = Array.isArray(canonicalDeal?.recent_activity)
    ? canonicalDeal.recent_activity.slice(0, 4)
    : Array.isArray(deal.recentActivity) ? deal.recentActivity.slice(0, 4) : [];

  return (
    <div
      data-testid="property-modal"
      className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[6vh] pb-6"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="Property snapshot"
    >
      {/* Calm operational animations — gated by reduced motion. */}
      <style>{`
        @keyframes prop-modal-fade-up {
          from { opacity: 0; transform: translateY(6px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes prop-modal-backdrop-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .prop-modal-shell    { animation: prop-modal-fade-up 220ms cubic-bezier(0.22, 0.61, 0.36, 1) both; }
        .prop-modal-backdrop { animation: prop-modal-backdrop-fade 180ms ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .prop-modal-shell,
          .prop-modal-backdrop { animation: none; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        data-testid="property-modal-backdrop"
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-[3px] prop-modal-backdrop"
      />

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[880px] max-h-[88vh] overflow-y-auto rounded-2xl bg-white prop-modal-shell"
        style={{
          boxShadow:
            "0 24px 60px -24px rgba(15,23,42,0.28), 0 0 0 1px rgba(226,232,240,0.6)",
        }}
        data-testid="property-modal-shell"
      >
        {/* Close */}
        <button
          data-testid="property-modal-close"
          onClick={close}
          aria-label="Close"
          className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 flex items-center justify-center transition-colors"
        >
          <X size={16} />
        </button>

        {/* ── 1 · Header ─────────────────────────────────────── */}
        <header className="px-6 pt-6 pb-4 grid grid-cols-1 sm:grid-cols-[128px_minmax(0,1fr)_auto] gap-5 items-start" data-testid="property-modal-hero">
          {deal.image ? (
            <img
              src={deal.image}
              alt={addressPrimary}
              className="h-[128px] w-[128px] rounded-xl object-cover bg-slate-100"
              loading="lazy"
            />
          ) : (
            <div className="h-[128px] w-[128px] rounded-xl bg-slate-100 flex items-center justify-center text-slate-400">
              <Home size={28} />
            </div>
          )}

          <div className="min-w-0">
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] px-2 h-[20px] rounded"
              style={{ background: "rgba(139,92,246,0.10)", color: "#6D28D9" }}
              data-testid="property-modal-stage-pill"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
              <span className="font-semibold opacity-70">Recorded stage ·</span>
              {stageLabel}
            </span>
            <h2
              className="font-display text-[22px] font-bold text-slate-900 mt-2 leading-[1.15] tracking-[-0.02em] truncate"
              data-testid="property-modal-title"
              title={addressPrimary}
            >
              {addressPrimary}
            </h2>
            {addressSubline && (
              <p className="text-[12px] text-slate-500 mt-1 inline-flex items-center gap-1.5 truncate">
                <MapPin size={11} className="text-slate-400 shrink-0" />
                <span className="truncate">{addressSubline}</span>
              </p>
            )}
            <p className="text-[12px] text-slate-600 mt-2 inline-flex items-center gap-2 flex-wrap" data-testid="property-modal-price-tenure">
              <span className="inline-flex items-center gap-1.5">
                <Tag size={11} className="text-slate-400" />
                <span className="font-semibold text-slate-800 tabular-nums">{priceLabel}</span>
              </span>
              {tenurePretty && (
                <>
                  <span aria-hidden className="text-slate-300">·</span>
                  <span>{tenurePretty}</span>
                </>
              )}
            </p>
          </div>

          <div className="flex flex-col items-end gap-1 pt-1" data-testid="property-modal-state-cluster">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 h-[24px] rounded-md ring-1 ${risk.ring} ${risk.bg} ${risk.text} text-[10.5px] font-bold tracking-[0.10em]`}
              data-testid="property-modal-risk"
            >
              <ShieldCheck size={12} />
              {risk.label}
            </span>
            <p className="text-[11px] text-slate-500 mt-1" data-testid="property-modal-last-chased">
              Last chased {lastChasedRel}
            </p>
            <button
              type="button"
              onClick={openActivity}
              className="text-[12px] font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1"
              data-testid="property-modal-view-activity"
            >
              View activity <ArrowRight size={11} />
            </button>
          </div>
        </header>

        {/* ── 2 · Compact parties row ────────────────────────── */}
        <div className="px-6" data-testid="property-modal-parties">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
            <PartyPill icon={User}      role={buyers.length > 1 ? "Buyers" : "Buyer"}   people={buyers}  onOpen={openContact} tone="violet"  testid="party-buyer" />
            <PartyPill icon={User}      role={sellers.length > 1 ? "Sellers" : "Seller"} people={sellers} onOpen={openContact} tone="pink"    testid="party-seller" />
            <PartyPill icon={Scale}     role="Buyer Solicitor"  people={buyerSolicitors}  onOpen={openContact} tone="amber"  testid="party-buyer-solicitor" />
            <PartyPill icon={Briefcase} role="Seller Solicitor" people={sellerSolicitors} onOpen={openContact} tone="orange" testid="party-seller-solicitor" />
            {brokers.length > 0 && (
              <PartyPill icon={Landmark} role="Broker" people={brokers} onOpen={openContact} tone="emerald" testid="party-broker" />
            )}
          </div>
        </div>

        {/* ── 3 · 3-column summary ───────────────────────────── */}
        <div className="px-6 mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Transaction Progress */}
          <section
            className="rounded-xl bg-white px-4 py-3.5"
            style={{ boxShadow: "0 0 0 1px rgba(226,232,240,0.7)" }}
            data-testid="property-modal-progress"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
              Milestones completed
            </p>
            <div className="mt-3 flex items-center gap-3">
              <SoftRing value={progressPct} size={72} stroke={5} color="#8B5CF6" />
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-slate-800 leading-tight" data-testid="property-modal-progress-count">
                  <span className="tabular-nums">{requiredDone}</span>
                  {requiredTotal > 0 && (
                    <>{" of "}<span className="tabular-nums">{requiredTotal}</span></>
                  )}
                </p>
                <p className="text-[11.5px] text-slate-500 leading-snug mt-0.5">
                  key stages complete
                </p>
              </div>
            </div>
            <p className="mt-2 text-[10.5px] text-slate-400 leading-snug" data-testid="property-modal-progress-explainer">
              {STAGE_PROGRESS_NOTE}
            </p>
          </section>

          {/* Predicted Exchange */}
          <section
            className="rounded-xl bg-white px-4 py-3.5"
            style={{ boxShadow: "0 0 0 1px rgba(226,232,240,0.7)" }}
            data-testid="property-modal-predicted-exchange"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 inline-flex items-center gap-1.5">
              <Calendar size={11} className="text-slate-400" /> Predicted Exchange
            </p>
            <p className="font-display text-[22px] font-bold text-slate-900 mt-2 tabular-nums leading-tight" data-testid="property-modal-predicted-label">
              {predictedLabel}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[12px] font-semibold text-slate-700 tabular-nums">{confidencePct}%</span>
              <span
                className={`inline-flex items-center px-1.5 h-[18px] rounded text-[10px] font-bold tracking-[0.08em] ${_confidenceToneClasses(confidencePct)}`}
                data-testid="property-modal-confidence-band"
              >
                {confidenceLabel}
              </span>
            </div>
            <div className="mt-1.5 h-1 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${confidencePct}%`,
                  background: _confidenceBarBackground(confidencePct),
                }}
              />
            </div>
          </section>

          {/* Target Dates */}
          <section
            className="rounded-xl bg-white px-4 py-3.5"
            style={{ boxShadow: "0 0 0 1px rgba(226,232,240,0.7)" }}
            data-testid="property-modal-target-dates"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 inline-flex items-center gap-1.5">
              <Calendar size={11} className="text-slate-400" /> Target Dates
            </p>
            <div className="mt-2.5 space-y-1.5">
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-slate-500">Exchange</span>
                <span className="font-semibold text-slate-800 tabular-nums" data-testid="property-modal-target-exchange">
                  {targetExchange || "—"}
                </span>
              </div>
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-slate-500">Completion</span>
                <span className="font-semibold text-slate-800 tabular-nums" data-testid="property-modal-target-completion">
                  {targetCompletion || "—"}
                </span>
              </div>
            </div>
          </section>
        </div>

        {/* ── 4 · Next Best Action ───────────────────────────── */}
        <div className="px-6 mt-3">
          <div
            className="rounded-xl px-4 py-3.5 flex items-center gap-3.5"
            style={{
              background: "linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(99,102,241,0.08) 100%)",
              boxShadow: "0 0 0 1px rgba(139,92,246,0.20)",
            }}
            data-testid="property-modal-nba"
          >
            <span
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0"
              style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6366F1 100%)" }}
            >
              <Sparkles size={16} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-violet-700">
                Next Best Action
              </p>
              <p className="font-display text-[14.5px] font-bold text-slate-900 leading-snug mt-0.5 truncate" data-testid="property-modal-nba-headline">
                {nbaHeadline}
              </p>
              <p className="text-[11.5px] text-slate-600 mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
                {waitingParty && (
                  <>
                    <span className="font-semibold text-slate-700">{waitingParty}</span>
                    <span aria-hidden className="text-slate-300">·</span>
                  </>
                )}
                {nbaWaitingDays > 0 ? (
                  <>
                    <span>Waiting {nbaWaitingDays} day{nbaWaitingDays > 1 ? "s" : ""}</span>
                    <span aria-hidden className="text-slate-300">·</span>
                  </>
                ) : null}
                <span>Last chased {lastChasedRel}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={openRoom}
              disabled={!hasRoom}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-white text-[12.5px] font-semibold transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              style={{
                background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
                boxShadow: "0 8px 20px -10px rgba(99,102,241,0.55)",
              }}
              data-testid="property-modal-nba-cta"
            >
              Chase now <ArrowRight size={12} />
            </button>
          </div>
        </div>

        {/* ── 5 · Recent activity (single shallow row) ───────── */}
        {activities.length > 0 && (
          <div className="px-6 mt-3" data-testid="property-modal-activity-row">
            <div
              className="rounded-xl bg-white px-4 py-3"
              style={{ boxShadow: "0 0 0 1px rgba(226,232,240,0.7)" }}
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-2">
                Recent Activity
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
                {activities.map((a, i) => (
                  <div key={a.id || i} className="flex items-center gap-2 min-w-0" data-testid={`property-modal-activity-${i}`}>
                    <span className={`h-7 w-7 rounded-full ${_activityToneClasses(a.kind).bg} ${_activityToneClasses(a.kind).text} flex items-center justify-center shrink-0`}>
                      {_activityIcon(a.kind)}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11.5px] font-semibold text-slate-800 truncate leading-tight">{a.label || a.title || "Update"}</p>
                      <p className="text-[10px] text-slate-500 truncate">{a.rel || _relFromIso(a.at) || ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── 6 · Footer ─────────────────────────────────────── */}
        <div className="px-6 py-4 mt-3 flex items-center gap-2 flex-wrap" data-testid="property-modal-footer">
          {hasRoom ? (
            <button
              data-testid="modal-open-room"
              onClick={openRoom}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-white text-[12.5px] font-semibold transition-all hover:brightness-110"
              style={{
                background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
                boxShadow: "0 10px 24px -10px rgba(99,102,241,0.55)",
              }}
            >
              <MessageSquare size={13} />
              Open Deal Room
              <ArrowRight size={12} />
            </button>
          ) : (
            <button
              data-testid="modal-create-room"
              onClick={startCreateDeal}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-white text-[12.5px] font-semibold transition-all hover:brightness-110"
              style={{
                background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
                boxShadow: "0 10px 24px -10px rgba(99,102,241,0.55)",
              }}
              aria-label={`Create deal room for ${addressPrimary}`}
            >
              <Plus size={13} />
              Create Deal Room
              <ArrowRight size={12} />
            </button>
          )}
          <button
            data-testid="modal-open-docs"
            onClick={openDocs}
            className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg bg-white text-slate-700 text-[12.5px] font-semibold transition-all hover:bg-slate-50"
            style={{ boxShadow: "0 0 0 1px rgba(226,232,240,0.9)" }}
          >
            <FileText size={13} />
            Documents
          </button>
          <button
            type="button"
            onClick={openRisk}
            className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg bg-white text-slate-700 text-[12.5px] font-semibold transition-all hover:bg-slate-50"
            style={{ boxShadow: "0 0 0 1px rgba(226,232,240,0.9)" }}
            data-testid="property-modal-risk-register"
          >
            <ShieldCheck size={13} />
            Risk register
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Helpers (module-local, no new state) ─────────────────────── */

const PARTY_TONES = {
  violet:  { icon: "bg-violet-50 text-violet-600" },
  pink:    { icon: "bg-pink-50 text-pink-600" },
  amber:   { icon: "bg-amber-50 text-amber-600" },
  orange:  { icon: "bg-orange-50 text-orange-600" },
  emerald: { icon: "bg-emerald-50 text-emerald-600" },
};

const PartyPill = ({ icon: Icon, role, people = [], onOpen, tone = "violet", testid }) => {
  const t = PARTY_TONES[tone] || PARTY_TONES.violet;
  return (
    <div
      className="flex items-start gap-2.5 px-3 py-2 rounded-lg bg-white min-w-0"
      style={{ boxShadow: "0 0 0 1px rgba(226,232,240,0.7)" }}
      data-testid={testid}
      data-count={people.length}
    >
      <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${t.icon}`}>
        <Icon size={13} />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] uppercase font-semibold tracking-[0.06em] text-slate-500 leading-tight break-words">{role}</p>
        {people.length === 0 && (
          <p className="text-[12.5px] font-semibold text-slate-800 leading-tight mt-0.5">—</p>
        )}
        {people.map((p, i) => (p.contactId ? (
          <button
            key={p.key || i}
            type="button"
            onClick={() => onOpen(p.contactId)}
            title={`Open ${p.name}'s contact profile`}
            className="block max-w-full text-left text-[12.5px] font-semibold text-slate-800 leading-tight break-words mt-0.5 underline decoration-slate-300 decoration-dotted underline-offset-[3px] hover:text-violet-700 hover:decoration-violet-400 hover:decoration-solid active:text-violet-800 touch-manipulation"
            data-testid={`${testid}-link-${p.contactId}`}
          >
            {p.name}
          </button>
        ) : (
          <p key={p.key || i} className="text-[12.5px] font-semibold text-slate-800 leading-tight break-words mt-0.5" title={p.name} data-testid={`${testid}-unlinked`}>
            {p.name}
          </p>
        )))}
      </div>
    </div>
  );
};

const _fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    if (!d || Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return null; }
};

const _relFromIso = (iso) => {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const ms = Date.now() - then;
    const min = Math.floor(ms / 60000);
    if (min < 1)   return "just now";
    if (min < 60)  return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return "Yesterday";
    if (days < 30)  return `${days}d ago`;
    return _fmtDate(iso);
  } catch { return null; }
};

const _priceLabel = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "string" && v.startsWith("£")) return v;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  if (Number.isFinite(n) && n > 0) return "£" + n.toLocaleString("en-GB");
  return String(v);
};

const _prettyStage = (s) => {
  if (!s) return "Under Offer";
  const raw = String(s).toLowerCase();
  const map = {
    offer: "Under Offer",
    aml: "AML & ID",
    memo: "Memo Issued",
    solicitors: "Solicitors Instructed",
    mortgage_app: "Mortgage Application",
    mortgage_offer: "Mortgage Offered",
    survey: "Survey",
    searches: "Searches",
    enquiries: "Enquiries",
    exchange_ready: "Ready to Exchange",
    exchanged: "Exchanged",
    completed: "Completed",
  };
  return map[raw] || s;
};

const _confidenceToneClasses = (pct) => {
  if (pct >= 75) return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/70";
  if (pct >= 50) return "bg-amber-50 text-amber-700 ring-1 ring-amber-200/70";
  if (pct > 0)   return "bg-rose-50 text-rose-700 ring-1 ring-rose-200/70";
  return "bg-slate-100 text-slate-600 ring-1 ring-slate-200/70";
};

const _confidenceBarBackground = (pct) => {
  if (pct >= 75) return "linear-gradient(90deg,#10B981 0%,#059669 100%)";
  if (pct >= 50) return "linear-gradient(90deg,#F59E0B 0%,#D97706 100%)";
  if (pct > 0)   return "linear-gradient(90deg,#F43F5E 0%,#E11D48 100%)";
  return "#E2E8F0";
};

const _activityToneClasses = (kind) => {
  switch (String(kind || "").toLowerCase()) {
    case "message":
    case "chase":     return { bg: "bg-violet-50",  text: "text-violet-600" };
    case "document":
    case "upload":    return { bg: "bg-emerald-50", text: "text-emerald-600" };
    case "silence":
    case "no_movement": return { bg: "bg-amber-50", text: "text-amber-600" };
    case "check":
    case "complete":  return { bg: "bg-emerald-50", text: "text-emerald-600" };
    default:          return { bg: "bg-slate-100", text: "text-slate-500" };
  }
};

const _activityIcon = (kind) => {
  switch (String(kind || "").toLowerCase()) {
    case "message":
    case "chase":     return <MessageSquare size={12} />;
    case "document":
    case "upload":    return <FileText size={12} />;
    case "silence":
    case "no_movement": return <Clock size={12} />;
    case "check":
    case "complete":  return <Check size={12} />;
    default:          return <Activity size={12} />;
  }
};


// Helper to dispatch from any component
export const openProperty = (deal) => {
  window.dispatchEvent(new CustomEvent("cf:property:open", { detail: deal }));
};

export default PropertyDetailsModal;
